import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import { getAdminApiBaseUrl } from './config';

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

const api: AxiosInstance = axios.create({
  baseURL: getAdminApiBaseUrl(),
  timeout: 900000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getErrorMessage = (error: AxiosError): string => {
  if (error.response?.data) {
    const data = error.response.data as Record<string, string>;
    return data.message || data.error || 'Erro interno, aguarde alguns instantes.';
  }

  if (error.request) {
    return 'Sua conexão falhou, por favor aguarde 1 minuto e tente acessar novamente.';
  }

  return error.message || 'Erro desconhecido, aguarde alguns instantes.';
};

const isAbortError = (error: AxiosError): boolean => {
  return error.name === 'CanceledError' ||
         error.message === 'canceled' ||
         error.code === 'ERR_CANCELED' ||
         error.message?.includes('abort');
};

const handleAutoLogout = () => {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_refresh_token');

  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// Request Interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor with token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean; _handleErrorLocally?: boolean };

    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('admin_refresh_token');

      if (refreshToken) {
        try {
          const response = await api.post('/auth/refresh-token', { refreshToken });
          const { token, refreshToken: newRefreshToken } = response.data.data;

          localStorage.setItem('admin_token', token);
          if (newRefreshToken) {
            localStorage.setItem('admin_refresh_token', newRefreshToken);
          }

          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } catch {
          handleAutoLogout();
          return Promise.reject(error);
        }
      } else {
        handleAutoLogout();
        return Promise.reject(error);
      }
    }

    if (error.response?.status === 403) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      return Promise.reject(error);
    }

    if (!originalRequest?._retry && !isAbortError(error) && !originalRequest?._handleErrorLocally) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    }

    return Promise.reject(error);
  }
);

export const apiGet = async <T = unknown>(url: string, config?: { signal?: AbortSignal }): Promise<T> => {
  const response = await api.get<ApiResponse<T>>(url, config);
  return response.data.data!;
};

export const apiPost = async <T = unknown>(url: string, data?: unknown, config?: { signal?: AbortSignal }): Promise<T> => {
  const response = await api.post<ApiResponse<T>>(url, data, config);
  return response.data.data!;
};

export const apiPut = async <T = unknown>(url: string, data?: unknown, config?: { signal?: AbortSignal }): Promise<T> => {
  const response = await api.put<ApiResponse<T>>(url, data, config);
  return response.data.data!;
};

export const apiPatch = async <T = unknown>(url: string, data?: unknown, config?: { signal?: AbortSignal }): Promise<T> => {
  const response = await api.patch<ApiResponse<T>>(url, data, config);
  return response.data.data!;
};

export const apiDelete = async (url: string, config?: { signal?: AbortSignal }): Promise<void> => {
  await api.delete(url, config);
};

export { api };
